import { supabase } from '@/integrations/supabase/client';
import {
  ControleSituacao,
  ControleSituacaoCreate,
  ControleSituacaoUpdate,
} from '@/types/controle-situacoes';

export class ControleSituacaoService {
  /**
   * Buscar todas as situações de controle
   */
  static async getAll(): Promise<ControleSituacao[]> {
    const { data, error } = await supabase
      .from('controle_situacoes')
      .select('*')
      .order('tipo_situacao', { ascending: true });

    if (error) {
      throw new Error(`Erro ao buscar situações: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Buscar uma situação específica por ID
   */
  static async getById(id: string): Promise<ControleSituacao | null> {
    const { data, error } = await supabase
      .from('controle_situacoes')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      throw new Error(`Erro ao buscar situação: ${error.message}`);
    }

    return data;
  }

  /**
   * Buscar uma situação específica por tipo
   */
  static async getByTipo(tipo_situacao: string): Promise<ControleSituacao | null> {
    const { data, error } = await supabase
      .from('controle_situacoes')
      .select('*')
      .eq('tipo_situacao', tipo_situacao)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // Não encontrado
        return null;
      }
      throw new Error(`Erro ao buscar situação: ${error.message}`);
    }

    return data;
  }

  /**
   * Criar uma nova situação de controle
   */
  static async create(situacao: ControleSituacaoCreate): Promise<ControleSituacao> {
    const { data, error } = await supabase
      .from('controle_situacoes')
      .insert({
        ...situacao,
        ativo: situacao.ativo ?? true,
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Erro ao criar situação: ${error.message}`);
    }

    return data;
  }

  /**
   * Atualizar uma situação de controle
   */
  static async update(id: string, updates: ControleSituacaoUpdate): Promise<ControleSituacao> {
    // Fazer o update SEM select para evitar problemas com RLS
    const { error: updateError } = await supabase
      .from('controle_situacoes')
      .update(updates)
      .eq('id', id);

    if (updateError) {
      throw new Error(`Erro ao atualizar situação: ${updateError.message}`);
    }

    // Depois do update, buscar o registro atualizado separadamente
    const { data, error: selectError } = await supabase
      .from('controle_situacoes')
      .select('*')
      .eq('id', id)
      .single();

    if (selectError) {
      // Se não conseguir buscar, pode ser que o update não funcionou ou há problema de RLS
      throw new Error(
        `Erro ao verificar atualização: ${selectError.message}. Verifique se o registro foi atualizado e as políticas RLS.`
      );
    }

    if (!data) {
      throw new Error(`Situação com ID ${id} não encontrada após atualização`);
    }

    return data;
  }

  /**
   * Atualizar múltiplas situações de controle (edição em massa)
   */
  static async updateMany(
    updates: Array<{ id: string; data: ControleSituacaoUpdate }>
  ): Promise<ControleSituacao[]> {
    // Atualizar uma por uma devido às limitações do Supabase
    const results: ControleSituacao[] = [];

    for (const update of updates) {
      try {
        const updated = await this.update(update.id, update.data);
        results.push(updated);
      } catch (error) {
        console.error(`Erro ao atualizar situação ${update.id}:`, error);
        throw error;
      }
    }

    return results;
  }

  /**
   * Deletar uma situação de controle
   */
  static async delete(id: string): Promise<void> {
    const { error } = await supabase.from('controle_situacoes').delete().eq('id', id);

    if (error) {
      throw new Error(`Erro ao deletar situação: ${error.message}`);
    }
  }
}
